import { rangeQueryPatternRegex } from './constants';
import { parseRelativeTimeString } from './relativeDateUtils';
import { safeArrayAccess } from './safeArrayAccess';
import type { RelativeDateFilter } from '../dto/searchModels';

/**
 * Parsed date range result
 */
export interface ParsedDateRange {
  start: RelativeDateFilter | null;
  end: RelativeDateFilter | null;
}

/**
 * Check if a string represents date range syntax
 * Range syntax format: "[start to end]"
 *
 * Examples:
 * - "[-7d to +0d]"
 * - "[2024-01-01 to 2024-12-31]"
 * - "[* to +30d]"
 *
 * @param value - The string to check
 * @returns True if the string matches date range syntax format
 */
export function isDateRangeSyntax(value: unknown): value is string {
  return typeof value === 'string' &&
    value.includes('[') &&
    value.includes(']') &&
    value.includes(' to ');
}

/**
 * Parse a single filter value (start or end)
 * Can be: absolute date, relative string, or wildcard
 *
 * @param value - The value to parse (e.g., "2025-01-01", "+5d", "*")
 * @returns RelativeDateFilter object or null for wildcard
 */
export function parseFilterValue(value: string): RelativeDateFilter | null {
  const trimmed = value.trim();

  // Wildcard - represents no boundary
  if (trimmed === '*') {
    return null;
  }

  // Try to parse as relative date
  const relativeParsed = parseRelativeTimeString(trimmed);
  if (relativeParsed) {
    return {
      type: 'relative',
      value: relativeParsed.value,
      unit: relativeParsed.unit,
      direction: relativeParsed.direction
    };
  }

  // Try to parse as absolute date
  try {
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return {
        type: 'absolute',
        absoluteDate: date
      };
    }
  } catch (e) {
    // Invalid date, continue
  }

  // Could not parse
  return null;
}

/**
 * Parse a date range string
 * Format: [start to end]
 *
 * Examples:
 * - [2025-01-01 to 2025-12-31]
 * - [+5d to +30d]
 * - [-7d to *]
 * - [* to 2025-12-31]
 *
 * @param rangeString - The range string to parse
 * @returns ParsedDateRange with start and end, or null if invalid format
 */
export function parseDateRangeString(rangeString: string): ParsedDateRange | null {
  if (!rangeString) {
    return null;
  }

  // Match the range pattern
  const match = rangeQueryPatternRegex.exec(rangeString);
  if (!match || match.length < 3) {
    return null;
  }

  const startStr = safeArrayAccess(match, 1).trim();
  const endStr = safeArrayAccess(match, 2).trim();

  return {
    start: parseFilterValue(startStr),
    end: parseFilterValue(endStr)
  };
}
